/**
 * ConfirmBookingPage
 *
 * Shows slot availability before confirming. If the selected slot is blocked,
 * displays alternative available slots. Prevents email link prefetchers from
 * auto-confirming by requiring a button click.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Phone, Mail, Loader, AlertTriangle, Clock } from 'lucide-react';

import API_BASE_URL from '../config/api';
const API_URL = API_BASE_URL;

function ConfirmBookingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slotInfo, setSlotInfo] = useState(null);
  const [selectedToken, setSelectedToken] = useState(token);

  // Check slot availability on load
  useEffect(() => {
    async function checkSlot() {
      try {
        const res = await fetch(`${API_URL}/booking/slot-info/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (data.success) {
          setSlotInfo(data);
          // If the clicked slot is blocked, don't pre-select it
          if (data.currentSlot && !data.currentSlot.available) {
            setSelectedToken(null);
          }
        } else {
          setError(data.error || 'Unable to load appointment details.');
        }
      } catch {
        // If the check fails, fall back to the old behaviour (just show confirm button)
        setSlotInfo(null);
      } finally {
        setLoading(false);
      }
    }
    checkSlot();
  }, [token]);

  const handleConfirm = async () => {
    const confirmToken = selectedToken || token;
    setConfirming(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/booking/confirm/${encodeURIComponent(confirmToken)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await res.json();

      if (result.success) {
        const params = new URLSearchParams({
          slot: result.slot?.dateFormatted || '',
          time: result.slot?.timeFormatted || '',
          autoBooked: result.autoBooked ? 'true' : 'false',
        });
        navigate(`/booking-confirmed?${params}`, { replace: true });
      } else if (result.alreadyBooked) {
        navigate('/booking-confirmed?already=true', { replace: true });
      } else if (result.slotTaken) {
        navigate('/booking-error?reason=taken', { replace: true });
      } else {
        navigate(`/booking-error?reason=${encodeURIComponent(result.error || 'unknown')}`, { replace: true });
      }
    } catch {
      setError('Unable to connect to the server. Please try again or contact reception.');
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: '#f5f5f5',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Loader size={40} color="#6b9bd1" style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const currentSlot = slotInfo?.currentSlot;
  const allSlots = slotInfo?.allSlots || [];
  const availableSlots = allSlots.filter(s => s.available);
  const isBlocked = currentSlot && !currentSlot.available;

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: '#f5f5f5',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: 8, padding: 40,
        maxWidth: 500, width: '100%', textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          backgroundColor: isBlocked ? '#fff3cd' : '#e3f2fd',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          {isBlocked ? (
            <AlertTriangle size={48} color="#e8941c" />
          ) : (
            <Calendar size={48} color="#6b9bd1" />
          )}
        </div>

        {isBlocked ? (
          <>
            <h1 style={{ color: '#333', marginBottom: 15 }}>
              Time No Longer Available
            </h1>
            <p style={{ color: '#666', marginBottom: 10, fontSize: 16 }}>
              Sorry, <strong>{currentSlot.dayName} {currentSlot.dateFormatted?.split(',')[1]?.trim()} at {currentSlot.timeFormatted}</strong> is no longer available.
            </p>
            {availableSlots.length > 0 ? (
              <>
                <p style={{ color: '#666', marginBottom: 20, fontSize: 16 }}>
                  Please select one of the remaining available times:
                </p>
                <div style={{ marginBottom: 20 }}>
                  {allSlots.map(slot => (
                    <button
                      key={slot.token}
                      onClick={() => slot.available && setSelectedToken(slot.token)}
                      disabled={!slot.available}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '14px 20px',
                        marginBottom: 8,
                        border: selectedToken === slot.token ? '2px solid #5cb85c' : '2px solid #e0e0e0',
                        borderRadius: 8,
                        backgroundColor: !slot.available ? '#f5f5f5' : selectedToken === slot.token ? '#f0fff0' : 'white',
                        cursor: slot.available ? 'pointer' : 'not-allowed',
                        opacity: slot.available ? 1 : 0.5,
                        textAlign: 'left',
                        fontSize: 15,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {slot.available ? (
                          <Clock size={18} color={selectedToken === slot.token ? '#5cb85c' : '#6b9bd1'} />
                        ) : (
                          <AlertTriangle size={18} color="#999" />
                        )}
                        <div>
                          <strong>{slot.dayName}, {slot.dateFormatted?.split(',')[1]?.trim()}</strong>
                          <span style={{ marginLeft: 10, color: slot.available ? '#333' : '#999' }}>
                            {slot.timeFormatted}
                          </span>
                        </div>
                        {!slot.available && (
                          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#999' }}>
                            {slot.passed ? 'Passed' : 'Unavailable'}
                          </span>
                        )}
                        {selectedToken === slot.token && (
                          <span style={{ marginLeft: 'auto', color: '#5cb85c', fontWeight: 'bold', fontSize: 13 }}>
                            Selected
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ color: '#d9534f', marginBottom: 20, fontSize: 16 }}>
                Unfortunately all proposed times are no longer available. Please contact reception to arrange an appointment.
              </p>
            )}
          </>
        ) : (
          <>
            <h1 style={{ color: '#333', marginBottom: 15 }}>
              Confirm Your Appointment
            </h1>
            {currentSlot && (
              <div style={{
                backgroundColor: '#e8f5e9', padding: 15, borderRadius: 8,
                marginBottom: 20, fontSize: 18,
              }}>
                <strong>{currentSlot.dayName}, {currentSlot.dateFormatted?.split(',')[1]?.trim()}</strong>
                <br />
                <span style={{ fontSize: 22, fontWeight: 'bold', color: '#2e7d32' }}>{currentSlot.timeFormatted}</span>
              </div>
            )}
            <p style={{ color: '#666', marginBottom: 25, fontSize: 16 }}>
              Click the button below to confirm your selected appointment time.
            </p>
          </>
        )}

        {error && (
          <div style={{
            backgroundColor: '#fee', border: '1px solid #d9534f',
            padding: 15, borderRadius: 8, marginBottom: 20,
            color: '#d9534f', fontSize: 14,
          }}>
            {error}
          </div>
        )}

        {(selectedToken || !isBlocked) && availableSlots.length > 0 && (
          <button
            onClick={handleConfirm}
            disabled={confirming || (!selectedToken && isBlocked)}
            style={{
              backgroundColor: confirming ? '#999' : '#5cb85c',
              color: 'white', border: 'none',
              padding: '15px 40px', borderRadius: 8,
              fontSize: 18, fontWeight: 'bold',
              cursor: confirming ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 10,
              marginBottom: 25,
            }}
          >
            {confirming ? (
              <>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Confirming...
              </>
            ) : (
              'Confirm Appointment'
            )}
          </button>
        )}

        <div style={{
          backgroundColor: '#f5f5f5', padding: 20,
          borderRadius: 8, marginTop: 10,
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: 10 }}>Need help?</p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <Phone size={18} />
            <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
          </p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Mail size={18} />
            <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
          </p>
        </div>

        <p style={{ marginTop: 25, fontSize: 14, color: '#999' }}>
          Anglesea Sports Medicine | Cambridge, New Zealand
        </p>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ConfirmBookingPage;
